import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import DataTable from "../../components/DataTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import TdLink from "../../components/TdLink";
import { FormCard, FormGrid } from "../../components/FormCard";
import { DetailRow, MonthRow, SelectRow } from "../../components/Rows";
import {
  getHubInvoices,
  deleteHubInvoice,
  sendHubInvoice,
  sendHubInvoices,
  generateCustomerInvoice,
  generatePartnerInvoice,
  getFreeInvoiceMonths,
} from "../../api/Invoices";
import { getAllCustomers } from "../../api/Customers";
import { getAllPartners } from "../../api/Partners";
import { useAuth } from "../../providers/AuthContext";
import { useTranslation } from "react-i18next";
import { getUiDateLocale } from "../../helpers/DateTime";
import toast from "react-hot-toast";

const invoiceStatusClass = (status) => {
  const classes = {
    CreatedInDb: "bg-primary-100 text-primary-700",
    NoCharge: "bg-mute-100 text-mute-700",
    Failed: "bg-error-100 text-error-700",
    FortnoxCheckRequired: "bg-kraft-100 text-kraft-800",
    Sending: "bg-secondary-100 text-secondary-800",
    SentToFortnox: "bg-success-100 text-success-800",
    Cancelling: "bg-kraft-100 text-kraft-800",
    Credited: "bg-volt-100 text-volt-700",
    Cancelled: "bg-mute-200 text-mute-800",
  };
  return classes[status] || "bg-mute-100 text-mute-700";
};

// The month an invoice is named after: its period month, or the month it was generated when the
// period spans several months (catch-up of older sessions). Returns "yyyy-MM" or "".
export const invoiceMonth = (invoice) => {
  const start = invoice?.periodStart ? String(invoice.periodStart).slice(0, 7) : "";
  const end = invoice?.periodEnd ? String(invoice.periodEnd).slice(0, 7) : "";
  if (start && (!end || start === end)) {
    return start;
  }
  return invoice?.createdDate ? String(invoice.createdDate).slice(0, 7) : start;
};

const invoiceTypeClass = (type) => {
  const classes = {
    CustomerInvoice: "bg-primary-100 text-primary-700",
    PartnerCustomerInvoice: "bg-secondary-100 text-secondary-800",
    PartnerSupplierInvoice: "bg-volt-100 text-volt-700",
  };
  return classes[type] || "bg-mute-100 text-mute-700";
};

// The old generator invoiced partners up to October 2026, so the hub starts with November 2026
const FIRST_PARTNER_MONTH = "2026-11";

const previousMonth = () => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${month}`;
};

const freeMonthGroups = (rows) => {
  const groups = [];
  rows.forEach((row) => {
    let group = groups.find((item) => item.year === row.year);
    if (!group) {
      group = { year: row.year, rows: [] };
      groups.push(group);
    }
    group.rows.push(row);
  });
  return groups;
};

const freeMonthName = (row, language) => {
  const name = new Date(row.year, row.month - 1, 1).toLocaleString(getUiDateLocale(language), { month: "short" });
  if (!name) {
    return String(row.month);
  }
  const text = name.replace(".", "");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Filters are kept while the user stays under /invoices, so going into an invoice and back keeps
// the list as it was. Leaving the invoice pages clears them.
const filtersKey = "invoiceListFilters";
export const useClearInvoiceFiltersOnLeave = () => {
  useEffect(() => () => {
    if (!window.location.pathname.startsWith("/invoices")) {
      try {
        sessionStorage.removeItem(filtersKey);
      } catch {
        // Storage can be blocked; nothing was saved then
      }
    }
  }, []);
};
const savedFilters = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(filtersKey) || "{}");
    return saved && typeof saved === "object" ? saved : {};
  } catch {
    return {};
  }
};

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { getAccessToken } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState(() => savedFilters().search ?? "");
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState([]);
  const [kind, setKind] = useState(() => savedFilters().kind ?? "customer");
  const [publicId, setPublicId] = useState(() => savedFilters().publicId ?? "");
  const [month, setMonth] = useState(() => {
    const initial = savedFilters().month ?? previousMonth();
    return initial < FIRST_PARTNER_MONTH ? FIRST_PARTNER_MONTH : initial;
  });
  const [freeMonths, setFreeMonths] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [noCharge, setNoCharge] = useState([]);
  const [failedParties, setFailedParties] = useState([]);
  const [progress, setProgress] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [parties, setParties] = useState([]);
  const [groupBy, setGroupBy] = useState(() => savedFilters().groupBy ?? "");
  const [confirmSend, setConfirmSend] = useState(null);
  useClearInvoiceFiltersOnLeave();

  useEffect(() => {
    try {
      sessionStorage.setItem(filtersKey, JSON.stringify({ kind, publicId, month, groupBy, search }));
    } catch {
      // Storage can be blocked; the filters then reset as before
    }
  }, [kind, publicId, month, groupBy, search]);

  const load = async () => {
    const accessToken = await getAccessToken();
    const rows = await getHubInvoices(accessToken);
    setInvoices(rows ?? []);
    setLoaded(true);
  };

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoaded(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  // Reset filters and results in the same render as the switch, so the table never filters
  // partner invoices by a customer id while the new party list is loading
  const changeKind = (value) => {
    if (value === kind) {
      return;
    }
    setKind(value);
    setParties([]);
    setPublicId("");
    setSelected([]);
    setSkipped([]);
    setNoCharge([]);
    setFailedParties([]);
    setFreeMonths([]);
  };

  useEffect(() => {
    const loadParties = async () => {
      const accessToken = await getAccessToken();
      const rows = kind === "customer" ? await getAllCustomers(accessToken) : await getAllPartners(accessToken);
      setParties(rows ?? []);
    };
    loadParties().catch((error) => console.error(error));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [kind]);

  const reasonLabel = (reason) => {
    const text = t(`invoice.reason.${reason.code}`, { detail: reason.detail ?? "" });
    if (reason.code === "supplierFetchFailed" && reason.detail) {
      return `${text}: ${reason.detail}`;
    }
    return text;
  };
  const reasonText = (row) => (row.cannotSend ?? []).map((reason) => reasonLabel(reason)).join(", ");

  const partyPublicId = (party) => (kind === "customer" ? party.customerPublicId : party.partnerPublicId);

  // One call per customer or partner, so no single request runs into the API gateway timeout
  const runGeneration = async (targets) => {
    const year = Number(month.slice(0, 4));
    const monthNumber = Number(month.slice(5, 7));
    const allSkipped = [];
    const allNoCharge = [];
    const failed = [];
    let created = 0;
    setFailedParties([]);
    setProgress({ done: 0, total: targets.length });
    const toastId = toast.loading(t("invoice.generating", { done: 0, total: targets.length }));
    for (let index = 0; index < targets.length; index += 1) {
      const party = targets[index];
      const id = partyPublicId(party);
      const name = party.name || id;
      try {
        const accessToken = await getAccessToken();
        const report = kind === "customer"
          ? await generateCustomerInvoice(id, accessToken)
          : await generatePartnerInvoice(id, year, monthNumber, accessToken);
        const rows = report?.created ?? [];
        created += rows.length;
        allSkipped.push(...(report?.skipped ?? []).map((row) => ({ ...row, partyName: name })));
        allNoCharge.push(...rows
          .filter((row) => row.status === "NoCharge" && row.noChargeReason)
          .map((row) => ({ ...row, partyName: name })));
      } catch (error) {
        failed.push(name);
        console.error(error);
      }
      setProgress({ done: index + 1, total: targets.length });
      toast.loading(t("invoice.generating", { done: index + 1, total: targets.length }), { id: toastId });
    }

    setSkipped(allSkipped);
    setNoCharge(allNoCharge);
    setFailedParties(failed);
    setProgress(null);
    if (failed.length === 0) {
      toast.success(t("invoice.generatedCount", { count: created }), { id: toastId });
    } else {
      toast.error(t("invoice.generatedWithErrors", { count: created, failed: failed.length }), { id: toastId });
    }

    await load().catch((error) => console.error(error));
  };

  const partyOptions = [
    { value: "", label: t("common.all") },
    ...parties
      .map((party) => {
        const id = kind === "customer" ? party.customerPublicId : party.partnerPublicId;
        if (!id) {
          return null;
        }
        return { value: id, label: party.name || id };
      })
      .filter((option) => option != null),
  ];

  const visibleInvoices = invoices.filter((row) => {
    const partnerInvoice = row.invoiceType === "PartnerCustomerInvoice" || row.invoiceType === "PartnerSupplierInvoice";
    if (kind === "customer" && row.invoiceType !== "CustomerInvoice") {
      return false;
    }
    if (kind === "partner" && !partnerInvoice) {
      return false;
    }
    if (!publicId) {
      return true;
    }
    if (row.partyPublicId) {
      return row.partyPublicId === publicId;
    }
    const selectedParty = parties.find((party) => partyPublicId(party) === publicId);
    return !!selectedParty?.name && row.partyName === selectedParty.name;
  });

  const skippedGroups = Object.values(skipped.reduce((groups, row) => {
    const partyName = row.partyName || "";
    const siteName = row.siteName || "";
    const key = `${partyName}|${siteName}|${row.code}`;
    const current = groups[key] || { key, partyName, code: row.code, siteName, count: 0 };
    current.count += 1;
    groups[key] = current;
    return groups;
  }, {})).sort((left, right) => left.partyName.localeCompare(right.partyName) || left.siteName.localeCompare(right.siteName));

  const columns = [
    {
      key: "select",
      widthClassName: "w-12 align-top whitespace-normal break-words",
      header: "",
      searchable: false,
      ignoreRowClick: true,
      render: (row) => (
        <label className={`inline-flex ${row.canSend ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}>
          <input
            type="checkbox"
            className="custom-checkbox-input hidden"
            disabled={!row.canSend}
            checked={selected.includes(row.invoiceId)}
            onChange={(event) => {
              setSelected((current) => event.target.checked
                ? [...current, row.invoiceId]
                : current.filter((id) => id !== row.invoiceId));
            }}
          />
          <span className="custom-checkbox h-5 w-5">
            <svg className="custom-checkbox-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </span>
        </label>
      ),
    },
    {
      key: "invoicePublicId",
      widthClassName: "w-36 align-top whitespace-normal break-words",
      header: t("invoice.publicId"),
      sortable: true,
      rawCell: true,
      getSortValue: (row) => row.thirdPartyInvoiceReferenceId || row.invoicePublicId || "",
      render: (row) => (
        <TdLink to={row.invoicePublicId ? `/invoices/${row.invoicePublicId}` : "/invoices"}>
          {row.thirdPartyInvoiceReferenceId || row.invoicePublicId || t("invoice.open")}
        </TdLink>
      ),
    },
    {
      key: "partyName",
      widthClassName: "align-top whitespace-normal break-words",
      header: t("invoice.party"),
      sortable: true,
      rawCell: true,
      getSortValue: (row) => row.partyName ?? "",
      render: (row) => (
        <TdLink to={row.invoicePublicId ? `/invoices/${row.invoicePublicId}` : "/invoices"}>
          {row.partyName || ""}
        </TdLink>
      ),
    },
    {
      key: "invoiceType",
      widthClassName: "align-top whitespace-nowrap",
      header: t("invoice.type"),
      sortable: true,
      getSortValue: (row) => row.invoiceType ?? "",
      render: (row) => (
        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${invoiceTypeClass(row.invoiceType)}`}>
          {t(`invoice.types.${row.invoiceType}`, { defaultValue: row.invoiceType || "" })}
        </span>
      ),
    },
    {
      key: "status",
      widthClassName: "w-44 align-top whitespace-normal break-words",
      header: t("invoice.status"),
      sortable: true,
      getSortValue: (row) => row.status ?? "",
      render: (row) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${invoiceStatusClass(row.status)}`}>
          {t(`invoice.statuses.${row.status}`, { defaultValue: row.status || "" })}
          {row.stuck ? ` (${t("invoice.stuck")})` : ""}
        </span>
      ),
    },
    {
      key: "periodStart",
      widthClassName: "w-56 align-top whitespace-normal break-words",
      header: t("invoice.period"),
      sortable: true,
      getSortValue: (row) => row.periodStart ?? "",
      render: (row) => {
        if (!row.periodStart) {
          return "";
        }
        const start = String(row.periodStart).slice(0, 10);
        const end = row.periodEnd ? String(row.periodEnd).slice(0, 10) : "";
        const range = end ? `${start} – ${end}` : start;
        const nameMonth = invoiceMonth(row);
        if (!nameMonth) {
          return range;
        }
        // The month name, with the dates below it
        const [year, month] = nameMonth.split("-").map(Number);
        const name = new Date(year, month - 1, 1).toLocaleString(getUiDateLocale(i18n.resolvedLanguage || i18n.language), { month: "long", year: "numeric" });
        return (
          <div>
            <span className="block font-semibold">{name.charAt(0).toUpperCase() + name.slice(1)}</span>
            <span className="block text-xs text-mute-500">{range}</span>
          </div>
        );
      },
    },
    {
      key: "amountExclVat",
      widthClassName: "w-32 align-top whitespace-normal break-words",
      header: t("invoice.amount"),
      sortable: true,
      getSortValue: (row) => row.amountExclVat ?? 0,
      render: (row) => row.amountExclVat ?? "",
    },
    {
      key: "cannotSend",
      widthClassName: "w-56 align-top whitespace-normal break-words",
      header: t("invoice.cannotSend"),
      render: (row) => reasonText(row),
    },
    {
      key: "actions",
      widthClassName: "w-24 align-top whitespace-normal break-words",
      header: "",
      sortable: false,
      searchable: false,
      rawCell: true,
      render: (row) => (
        <td className="text-center" onClick={(event) => event.stopPropagation()}>
          <div className="flex justify-end items-center gap-3">
            {row.canSend && (
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-full text-primary-700 transition-colors hover:bg-primary-50 hover:text-primary-500"
                title={t("invoice.send")}
                onClick={() => setConfirmSend([row.invoiceId])}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="text-base" />
              </button>
            )}
            {row.canDelete && (
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-full text-error-500 transition-colors hover:bg-error-100 hover:text-error-600"
                title={t("invoice.delete")}
                onClick={() => setPendingDelete(row.invoiceId)}
              >
                <FontAwesomeIcon icon={faTrashCan} className="text-base" />
              </button>
            )}
          </div>
        </td>
      ),
    },
  ];

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage title={t("invoice.title")} loading={!loaded} />
          </div>
          <div className={`${!loaded ? "loading" : ""} page-content`}>
            <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
              <h2 className="text-2xl text-black font-bold">{t("invoice.title")}</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={progress != null || (!publicId && parties.length === 0)}
                  onClick={() => {
                    if (!publicId) {
                      setConfirmAll(true);
                      return;
                    }
                    runGeneration(parties.filter((party) => partyPublicId(party) === publicId));
                  }}
                >
                  {t("invoice.generate")}
                </button>
                {kind === "partner" && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={!publicId}
                    onClick={async () => {
                      try {
                        const accessToken = await getAccessToken();
                        const rows = await getFreeInvoiceMonths(publicId, accessToken);
                        setFreeMonths(rows ?? []);
                      } catch (error) {
                        toast.error(t("invoice.freeSessionsFailed"));
                        console.error(error);
                      }
                    }}
                  >
                    {t("invoice.freeSessions")}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={selected.length === 0}
                  onClick={() => setConfirmSend(selected)}
                >
                  {t("invoice.sendSelected")}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <FormCard>
                <FormGrid>
                  <DetailRow
                    label={t("invoice.type")}
                    value={(
                      <span className="inline-flex overflow-hidden rounded-full border border-primary-200">
                        {[
                          { value: "customer", label: t("invoice.customer") },
                          { value: "partner", label: t("invoice.partner") },
                        ].map((option) => {
                          const active = kind === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              disabled={progress != null}
                              className={`h-8 px-4 text-sm font-semibold leading-8 disabled:opacity-50 ${active ? "bg-primary-300 text-primary-700" : "bg-white text-mute-700 hover:bg-primary-50"}`}
                              onClick={() => changeKind(option.value)}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </span>
                    )}
                  />
                  <SelectRow
                    label={t("invoice.party")}
                    value={publicId}
                    options={partyOptions}
                    onChange={(value) => {
                      setPublicId(value || "");
                      setSelected([]);
                    }}
                  />
                  <DetailRow
                    label={t("invoice.groupBy")}
                    value={(
                      <span className="flex flex-wrap items-center gap-2">
                        {[
                          { value: "", label: t("invoice.groupNone") },
                          { value: "partyName", label: kind === "customer" ? t("entity.customer") : t("entity.partner") },
                          { value: "status", label: t("invoice.status") },
                        ].map((option) => {
                          const active = groupBy === option.value;
                          return (
                            <button
                              key={option.value || "none"}
                              type="button"
                              className={`inline-flex h-7 items-center rounded-full border px-3 text-xs font-semibold ${active ? "border-primary-300 bg-primary-300 text-primary-700" : "border-primary-200 bg-white text-primary-700 hover:bg-primary-50"}`}
                              onClick={() => setGroupBy(option.value)}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </span>
                    )}
                  />
                  {kind === "partner" && (
                    <MonthRow
                      label={t("invoice.month")}
                      value={month}
                      min={FIRST_PARTNER_MONTH}
                      max={previousMonth()}
                      onChange={setMonth}
                    />
                  )}
                </FormGrid>
              </FormCard>
          {freeMonths.length > 0 && (
            <FormCard title={t("invoice.freeSessions")}>
              <div className="flex flex-col gap-3">
                {freeMonthGroups(freeMonths).map((group) => (
                  <div key={group.year} className="flex flex-wrap items-center gap-2">
                    <span className="w-12 text-sm font-semibold">{group.year}</span>
                    {group.rows.map((row) => {
                      const type = row.driftavtal && row.sjalvfaktura
                        ? t("invoice.both")
                        : row.driftavtal ? t("invoice.driftavtal") : t("invoice.sjalvfaktura");
                      return (
                        <span key={`${row.year}-${row.month}`} className="rounded-md border border-mute-200 px-2 py-1 text-sm">
                          {freeMonthName(row, i18n.resolvedLanguage || i18n.language)} · {type}
                        </span>
                      );
                    })}
                  </div>
                ))}
              </div>
            </FormCard>
          )}
          {failedParties.length > 0 && (
            <p className="text-sm">{t("invoice.generateFailedFor", { parties: failedParties.join(", ") })}</p>
          )}
          {noCharge.length > 0 && (
            <FormCard title={t("invoice.noChargeTitle")}>
              <ul className="text-sm">
                {noCharge.map((row) => (
                  <li key={row.invoiceId}>
                    <NavLink to={row.invoicePublicId ? `/invoices/${row.invoicePublicId}` : "/invoices"} className="font-semibold text-primary-700">
                      {t(`invoice.noChargeReason.${row.noChargeReason}`, {
                        party: row.partyName,
                        type: t(`invoice.types.${row.invoiceType}`, { defaultValue: row.invoiceType || "" }),
                        count: row.sessionCount,
                      })}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </FormCard>
          )}
          {skippedGroups.length > 0 && (
            <FormCard title={t("invoice.skipped")}>
              <table className="table-default w-full">
                <thead>
                  <tr>
                    <th>{t("invoice.party")}</th>
                    <th>{t("entity.site")}</th>
                    <th>{t("invoice.skipReason")}</th>
                    <th>{t("entity.sessions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {skippedGroups.map((group) => (
                    <tr key={group.key}>
                      <td>{group.partyName}</td>
                      <td>{group.siteName}</td>
                      <td>{t(`invoice.reason.${group.code}`, { defaultValue: group.code })}</td>
                      <td>{group.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </FormCard>
          )}
          <div className="rounded-md shadow-md w-full p-4">
            {(() => {
              const sections = [];
              if (!groupBy) {
                sections.push({ key: "all", label: "", rows: visibleInvoices });
              } else {
                const groups = new Map();
                visibleInvoices.forEach((row) => {
                  const value = String(row[groupBy] || "");
                  const current = groups.get(value) || [];
                  current.push(row);
                  groups.set(value, current);
                });
                [...groups.keys()].sort((left, right) => left.localeCompare(right, "sv")).forEach((value) => {
                  const label = groupBy === "status"
                    ? t(`invoice.statuses.${value}`, { defaultValue: value })
                    : value;
                  sections.push({ key: value || "none", label, rows: groups.get(value) });
                });
              }
              if (sections.length === 0) {
                sections.push({ key: "all", label: "", rows: [] });
              }
              return sections.map((section, index) => (
                <div key={section.key} className="mb-6">
                  {section.label ? <h2 className="mb-2 inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-sm font-semibold text-primary-700">{section.label}</h2> : null}
                  <DataTable
                    rows={section.rows}
                    columns={columns}
                    tableClassName="table-default w-full table-fixed"
                    tableMinWidthClassName="min-w-[1100px]"
                    search={search}
                    onSearchChange={index === 0 ? setSearch : undefined}
                    searchPlaceholder={t("invoice.search")}
                    defaultSortColumn="invoicePublicId"
                    rowKey={(row, index) => row.invoiceId || index}
                    emptyColSpan={9}
                    emptyContent={t("invoice.empty")}
                    onRowClick={(row) => {
                      if (row.invoicePublicId) {
                        navigate(`/invoices/${row.invoicePublicId}`);
                      }
                    }}
                  />
                </div>
              ));
            })()}
          </div>
          <ConfirmationModal
            isOpen={confirmAll}
            onClose={() => setConfirmAll(false)}
            title={t("invoice.generateAll")}
            description={kind === "customer"
              ? t("invoice.generateAllConfirmCustomers", { count: parties.length })
              : t("invoice.generateAllConfirmPartners", { count: parties.length, month })}
            okText={t("invoice.generate")}
            onConfirm={() => {
              setConfirmAll(false);
              runGeneration(parties.filter((party) => partyPublicId(party)));
            }}
          />
          <ConfirmationModal
            isOpen={confirmSend != null}
            icon={faPaperPlane}
            tone="primary"
            onClose={() => setConfirmSend(null)}
            title={t("invoice.send")}
            description={(() => {
              const ids = confirmSend ?? [];
              const rows = invoices.filter((row) => ids.includes(row.invoiceId) && row.invoiceType === "PartnerSupplierInvoice");
              if (ids.length === 1) {
                return rows.length === 1
                  ? t("invoice.sendConfirmSupplier", { party: rows[0].partyName || "", email: rows[0].supplierEmail || t("invoice.supplierEmailMissing") })
                  : t("invoice.sendConfirm");
              }
              return (
                <>
                  <span className="block">{t("invoice.sendConfirmMany", { count: ids.length })}</span>
                  {rows.map((row) => (
                    <span key={row.invoiceId} className="block mt-1">
                      {row.partyName || ""}
                      {": "}
                      {row.supplierEmail ? row.supplierEmail : <span className="text-error-600">{t("invoice.supplierEmailMissing")}</span>}
                    </span>
                  ))}
                </>
              );
            })()}
            okText={t("invoice.send")}
            onConfirm={async () => {
              const ids = confirmSend ?? [];
              setConfirmSend(null);
              const toastId = toast.loading(t("invoice.sending"));
              try {
                const accessToken = await getAccessToken();
                if (ids.length === 1) {
                  await sendHubInvoice(ids[0], accessToken);
                  toast.success(t("invoice.sent"), { id: toastId });
                } else {
                  const sent = await sendHubInvoices(ids, accessToken);
                  const count = Array.isArray(sent) ? sent.length : 0;
                  if (count === ids.length) {
                    toast.success(t("invoice.sentMany", { count }), { id: toastId });
                  } else if (count === 0) {
                    toast.error(t("invoice.sendFailed"), { id: toastId });
                  } else {
                    toast.error(t("invoice.sentPartial", { sent: count, total: ids.length }), { id: toastId });
                  }
                }
                setSelected([]);
                await load();
              } catch (error) {
                toast.error(t("invoice.sendFailed"), { id: toastId });
                console.error(error);
              }
            }}
          />
          <ConfirmationModal
            isOpen={pendingDelete != null}
            onClose={() => setPendingDelete(null)}
            title={t("invoice.delete")}
            description={t("invoice.deleteConfirm")}
            okText={t("invoice.delete")}
            onConfirm={async () => {
              const invoiceId = pendingDelete;
              setPendingDelete(null);
              const toastId = toast.loading(t("invoice.deleting"));
              try {
                const accessToken = await getAccessToken();
                await deleteHubInvoice(invoiceId, accessToken);
                toast.success(t("invoice.deleted"), { id: toastId });
                await load();
              } catch (error) {
                toast.error(t("invoice.deleteFailed"), { id: toastId });
                console.error(error);
              }
            }}
          />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default InvoicesPage;
